import { CheckBox } from "@portal/BaseComponents/CheckBox/AntCheckBox";
import { onCheckBoxChange } from "@portal/BaseComponents/CheckBox/type";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import {
  Form,
  useForm,
  useFormWatch,
} from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { InvoiceLineItems } from "@portal/BaseComponents/InvoiceLineItems/InvoiceLineItems";
import { LineItemProps } from "@portal/BaseComponents/InvoiceLineItems/type";
import { InvoiceSearchWrapper } from "@portal/BaseComponents/InvoicesSearch/InvoiceSearchWrapper";
import { onSelectedValuesChange } from "@portal/BaseComponents/InvoicesSearch/type";
import { Modal } from "@portal/BaseComponents/Modal/AntModal";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormFooter } from "@portal/BaseComponents/UI/FormArea/FormFooter";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { useUpdateDisputeDetailsQuery } from "@portal/queries/dispute-query";
import {
  CreateDispute,
  DisputeDetails,
  disputeFiledIds,
} from "@portal/types/entities/dispute";
import { useState } from "react";
import {
  DisputeDescription,
  DisputeTitle,
  DisputeTypesForm,
} from "../DisputeFormComponents";
import CustomFieldFormItem from "../CustomFieldEditor/CustomFieldFormItem";
import { EDIT_MODAL_ZINDEX } from "@portal/BaseComponents/Drawer/predicate";

const t = {
  lineItem: "Refer a Line Item",
};

interface DisputeEditProps {
  open: boolean;
  onClose: () => void;
  getUpdatedValues?: (value: CreateDispute) => void;
  value?: DisputeDetails;
  onOpenChange?: (open: boolean) => void;
}

function DisputeEdit(props: DisputeEditProps) {
  const { open, onClose, value } = props;

  const [isLineItem, setIsLineItem] = useState<
    Partial<LineItemProps> | undefined
  >({
    isLineItem: value?.invoices.length === 1,
    refer: Boolean(value?.invoice_line_items.length),
    title: value?.invoices[0].number,
  });

  const [form] = useForm<CreateDispute>();
  const invoice_ids = useFormWatch([disputeFiledIds.invoice_ids], form);
  const updateDispute = useUpdateDisputeDetailsQuery({
    successCallBack() {
      handleClose();
    },
  });
  function handleClose() {
    onClose();
    setIsLineItem(undefined);
  }
  async function createDispute(value: CreateDispute) {
    updateDispute.mutateAsync(value);
  }
  function handleSubmit() {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      createDispute(values);
    });
  }

  function handleInvoiceSearch(...args: onSelectedValuesChange) {
    const [value, option] = args;
    form.setFieldValue(disputeFiledIds.invoice_ids, value);
    setIsLineItem((previousValues) => {
      return {
        ...previousValues,
        isLineItem: value.length === 1,
        title: option?.length ? option?.[0].number : "",
        refer: previousValues?.refer
          ? value.length === 1
          : previousValues?.refer,
      };
    });
  }

  function handleReferItemChange(event: onCheckBoxChange[number]) {
    const updatePredicate = (
      previousValues: Partial<LineItemProps> | undefined
    ) => {
      return { ...previousValues, refer: event.target.checked };
    };
    setIsLineItem(updatePredicate);

    if (!event.target.checked) {
      form.setFieldValue(disputeFiledIds.invoice_line_item_ids, []);
    }
  }

  // function handleUploadChange(info: OnUploadChange<Files>) {
  //   const selectedValues: string[] = form.getFieldValue("file_upload_ids");
  //   const updatedValues = produce(selectedValues, (draft) => {
  //     if (info.file.response) {
  //       draft.push(info.file.response.id);
  //     }
  //   });
  //   form.setFieldValue("file_upload_ids", updatedValues);
  // }

  const HeaderTitle = (
    <Flex align="center" justify="start" space="8">
      <TypographyText fs="20">Edit Dispute</TypographyText>
    </Flex>
  );

  const Footer = (
    <FormFooter
      cancelButtonProps={{ onClick: handleClose }}
      saveButtonProps={{
        onClick: handleSubmit,
        loading: updateDispute.isLoading,
      }}
    />
  );

  const Header = (
    <FormHeader title={HeaderTitle} onClose={onClose} mode="modal" />
  );

  const AssociatedInvoice = (
    <StyledFormItem
      label="Add Invoices"
      required
      initialValue={value?.invoices.map((f) => f.id)}
      style={{ width: "100%" }}
      preserve={false}
      name={disputeFiledIds.invoice_ids}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <InvoiceSearchWrapper
        selectedValuesCallBack={handleInvoiceSearch}
        initialValue={value?.invoices}
        openInvoicesOnly
      />
    </StyledFormItem>
  );

  const DisputeTypes = (
    <StyledFormItem
      label="Dispute Type"
      space="sp-1"
      required
      preserve={false}
      initialValue={value?.dispute_types.map((f) => f.id)}
      name={disputeFiledIds.dispute_type_ids}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <DisputeTypesForm disableFilterOptions />
    </StyledFormItem>
  );

  const Title = (
    <StyledFormItem
      label="Title"
      space="sp-1"
      required
      preserve={false}
      initialValue={value?.title}
      name={disputeFiledIds.title}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <DisputeTitle />
    </StyledFormItem>
  );

  const Description = (
    <StyledFormItem
      label="Description"
      space="sp-1"
      preserve={false}
      name={disputeFiledIds.description}
      initialValue={value?.description}
    >
      <DisputeDescription />
    </StyledFormItem>
  );

  // const Attachments = (
  //   <StyledFormItem
  //     space="sp-1"
  //     label="Attachments"
  //     preserve={false}
  //     name={disputeFiledIds.file_upload_ids}
  //     initialValue={value?.attachments}
  //     getValueFromEvent={normFile}
  //     valuePropName="fileList"
  //   >
  //     <DocUploader
  //       action="/api/v1/attachments"
  //       onChange={handleUploadChange}
  //       previewType="EXPIRE_LINK"
  //     />
  //   </StyledFormItem>
  // );
  const LineItem = (
    <StyledFormItem
      label="Select Line Item of the Invoice"
      name={disputeFiledIds.invoice_line_item_ids}
      space="sp-1"
      initialValue={value?.invoice_line_items.map((f) => f.id)}
      preserve={false}
    >
      <InvoiceLineItems
        title={isLineItem?.title}
        selectedInvoices={invoice_ids}
      />
    </StyledFormItem>
  );

  const DisputeStatus = (
    <StyledFormItem
      name={disputeFiledIds.status}
      initialValue={value?.status}
      hidden
    />
  );

  const ReferLineItem = (
    <CheckBox
      design="border-design"
      style={{ width: "200px" }}
      onChange={handleReferItemChange}
      checked={isLineItem?.refer}
      disabled={!isLineItem?.isLineItem}
    >
      {t.lineItem}
    </CheckBox>
  );

  const InvoiceArea = (
    <Flex
      align="start"
      space="8"
      justify="start"
      vertical
      style={{ marginBottom: "var(--space-32)" }}
    >
      {AssociatedInvoice}
      {ReferLineItem}
    </Flex>
  );

  const CustomField = (
    <CustomFieldFormItem customFields={value?.custom_field_data} form={form} />
  );
  return (
    <Modal
      open={open}
      title={Header}
      width={640}
      style={{
        transform: "none",
      }}
      destroyOnClose={true}
      mask={true}
      onClose={handleClose}
      closable={false}
      footer={Footer}
      centered
      onCancel={onClose}
      zIndex={EDIT_MODAL_ZINDEX}
      maskClosable={true}
      styles={{
        body: {
          overflow: "auto",
          maxHeight: "600px",
        },
      }}
    >
      <Form form={form} layout="vertical" preserve={false}>
        {InvoiceArea}
        {isLineItem?.refer ? LineItem : null}
        {DisputeTypes}
        {Title}
        {Description}
        {/* {Attachments} */}
        {DisputeStatus}
        {CustomField}
      </Form>
    </Modal>
  );
}

export { DisputeEdit };
